import { SpinningLoader, Text } from '@innovationdepartment/proxima-ui';
import { useEffect, useRef, useState } from 'react';
import type { ExecutionDetails } from '../BrandWorkflows.types';
import useBrandWorkflowsApi from '../useBrandWorkflowsApi';
import { PendingState } from './WorkflowTasksDetails.styles';
import WorkflowTasksDetailsView from './WorkflowTasksDetails.View';

type WorkflowTasksDetailsProps = {
  executionId: string;
};

/* Renders one execution's detail; mounted lazily by the Status-row expand */
const WorkflowTasksDetails = ({ executionId }: WorkflowTasksDetailsProps) => {
  const { getExecution } = useBrandWorkflowsApi();
  const getExecutionRef = useRef(getExecution);
  getExecutionRef.current = getExecution;
  const [execution, setExecution] = useState<ExecutionDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setExecution(null);
    setError(null);

    const load = async () => {
      try {
        const result = await getExecutionRef.current(executionId);
        if (cancelled) return;
        if (result.data) setExecution(result.data);
        else setError(result.error);
      } catch {
        if (!cancelled) setError('Unexpected error');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [executionId]);

  if (error) {
    return (
      <PendingState>
        <Text variant="body2" color="red60">
          Failed to load execution details: {error}
        </Text>
      </PendingState>
    );
  }

  if (!execution) {
    return (
      <PendingState>
        <SpinningLoader />
      </PendingState>
    );
  }

  return <WorkflowTasksDetailsView execution={execution} />;
};

export default WorkflowTasksDetails;
